import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Monitor, UserPlus, Loader2, Store, UtensilsCrossed } from "lucide-react";
import { mxn } from "@/lib/api";
import { planMeta, PLAN_ORDER, PRODUCT_IDS, PERIOD_LABEL, PERIOD_SUFFIX, usersText } from "@/lib/plans";

const ICONS = { profesionalpos: Store, profesionalpos_restaurante: UtensilsCrossed };

function PlanCard({ catalog, productId, planId, plan, period, onBuy, index }) {
  const meta = planMeta(catalog, productId, planId);
  const featured = planId === "pro";
  const monthlyEq = period === "annual" ? Math.round(plan.annual / 12) : null;
  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-7 animate-fade-up ${featured ? "border-2 border-foreground bg-foreground text-white shadow-2xl lg:-translate-y-3" : "border-border bg-card"}`}
      style={{ animationDelay: `${index * 90}ms` }}
      data-testid={`plan-card-${productId}-${planId}`}
    >
      {meta.badge && <span className="absolute right-5 top-5 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white">{meta.badge}</span>}
      <p className={`overline ${featured ? "text-white/60" : "text-muted-foreground"}`}>Plan</p>
      <h3 className="mt-1 font-display text-2xl font-bold tracking-tight">{plan.name}</h3>
      <div className="mt-5 flex items-end gap-1.5">
        <span className="font-display text-4xl font-black tracking-tighter" data-testid={`plan-price-${productId}-${planId}`}>{mxn(plan[period])}</span>
        <span className={`mb-1.5 text-sm ${featured ? "text-white/50" : "text-muted-foreground"}`}>{PERIOD_SUFFIX[period]}</span>
      </div>
      {monthlyEq && <p className={`mt-1 text-xs ${featured ? "text-white/50" : "text-muted-foreground"}`}>Equivale a {mxn(monthlyEq)} al mes</p>}
      <div className={`mt-5 flex flex-wrap gap-2 text-xs font-medium ${featured ? "text-white/80" : "text-foreground"}`}>
        <span className={`rounded-full px-3 py-1 ${featured ? "bg-white/10" : "bg-secondary"}`}>{plan.terminals} terminal{plan.terminals > 1 ? "es" : ""}{plan.terminals > 1 ? " / sucursales" : " base"}</span>
        <span className={`rounded-full px-3 py-1 ${featured ? "bg-white/10" : "bg-secondary"}`}>{usersText(plan.users)}</span>
        {plan.multiempresa && plan.users != null && <span className={`rounded-full px-3 py-1 ${featured ? "bg-white/10" : "bg-secondary"}`}>Multiempresa</span>}
      </div>
      {meta.intro && <p className="mt-6 text-sm font-semibold text-primary">{meta.intro}</p>}
      <ul className={`mt-3 flex-1 space-y-2.5 text-sm ${featured ? "text-white/75" : "text-muted-foreground"}`}>
        {meta.features.map((f, k) => (
          <li key={`${k}-${f}`} className="flex items-start gap-2.5"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" strokeWidth={2.5} />{f}</li>
        ))}
      </ul>
      <Button
        size="lg"
        onClick={() => onBuy(planId)}
        className={`mt-8 w-full rounded-full ${featured ? "bg-primary hover:bg-primary/90" : ""}`}
        variant={featured ? "default" : "outline"}
        data-testid={`plan-buy-${productId}-${planId}`}
      >
        Comprar {plan.name}
      </Button>
    </div>
  );
}

export function PlansSection({ catalog, product, setProduct, period, setPeriod }) {
  const navigate = useNavigate();
  const buy = (plan) => navigate(`/comprar?product=${product}&plan=${plan}&period=${period}`);
  const prod = catalog?.products[product];

  return (
    <section id="precios" className="border-y border-border bg-secondary/40">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="overline text-primary">Planes y módulos</p>
            <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tight md:text-5xl">Un plan para cada etapa de tu negocio</h2>
            <p className="mt-4 text-muted-foreground">Paga mensual o anual. Agrega terminales o usuarios cuando los necesites.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="plans-product-toggle">
              {PRODUCT_IDS.map((id) => {
                const Icon = ICONS[id];
                return (
                  <button key={id} onClick={() => setProduct(id)} data-testid={`plans-product-${id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${product === id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
                    <Icon className="h-4 w-4" />{id === "profesionalpos" ? "ProfesionalPOS" : "Restaurante"}
                  </button>
                );
              })}
            </div>
            <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="plans-period-toggle">
              {["monthly", "annual"].map((p) => (
                <button key={p} onClick={() => setPeriod(p)} data-testid={`plans-period-${p}`}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${period === p ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  {PERIOD_LABEL[p]}
                </button>
              ))}
            </div>
          </div>
        </div>

        {!catalog ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <>
            <div key={product + period} className="mt-14 grid gap-6 lg:grid-cols-3">
              {PLAN_ORDER.map((id, i) => (
                <PlanCard key={id} index={i} catalog={catalog} productId={product} planId={id} plan={prod.plans[id]} period={period} onBuy={buy} />
              ))}
            </div>
            <div className="mt-10 flex flex-col gap-4 rounded-2xl border border-dashed border-border bg-card p-6 sm:flex-row sm:items-center sm:gap-10" data-testid="addons-info">
              <p className="overline text-muted-foreground">Extras en cualquier plan</p>
              <span className="flex items-center gap-2 text-sm"><Monitor className="h-4 w-4 text-primary" /> Terminal adicional: <strong>{mxn(catalog.addons.terminal.monthly)}/mes</strong> o <strong>{mxn(catalog.addons.terminal.annual)}/año</strong></span>
              {prod.allows_extra_users && (
                <span className="flex items-center gap-2 text-sm"><UserPlus className="h-4 w-4 text-primary" /> Usuario adicional: <strong>{mxn(catalog.addons.user.monthly)}/mes</strong> o <strong>{mxn(catalog.addons.user.annual)}/año</strong></span>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
